import { useState, useEffect, useCallback, Fragment } from 'react';
import { 
  Package, Search, AlertTriangle, Eye, Trash2, RefreshCw, History, Download, FileText, ChevronRight, ChevronDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { useAuth } from '@/contexts/AuthContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { inventoryAPI } from '@/services/api';
import api from '@/services/api';
import { EXPIRY_FILTERS, expiryParams, exportRowsToCsv } from '@/utils/stockExport';
import { formatDateOnly, formatDate } from '@/utils/dateFormat';

const WarehouseStockPage = () => {
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('active');
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState('product');
  const [batchSearch, setBatchSearch] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [includeZero, setIncludeZero] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState([]);
  
  // Movement history dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [movements, setMovements] = useState([]);
  const [movementsLoading, setMovementsLoading] = useState(false);
  
  const { user } = useAuth();
  const { toast } = useToast();
  const warehouseId = user?.entity_id;

  const fetchStock = useCallback(async () => {
    if (!warehouseId) return;
    
    setLoading(true);
    try {
      const response = await inventoryAPI.getWarehouseStock(warehouseId, {
        view: activeView,
        group_by: groupBy,
        search: search || undefined,
        batch_search: groupBy === 'batch' && batchSearch ? batchSearch : undefined,
        include_zero: groupBy === 'batch' ? includeZero : undefined,
        ...(groupBy === 'batch' ? expiryParams(expiryFilter) : {})
      });
      setStockData(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch stock data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, activeView, search, groupBy, batchSearch, expiryFilter, includeZero, toast]);

  const toggleProductBatches = async (item) => {
    if (expandedProduct === item.product_id) {
      setExpandedProduct(null);
      setExpandedBatches([]);
      return;
    }
    setExpandedProduct(item.product_id);
    setExpandedBatches([]);
    try {
      const res = await inventoryAPI.getProductBatches(item.product_id, warehouseId);
      setExpandedBatches(res.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch batches', variant: 'destructive' });
    }
  };

  const handleExport = () => {
    const rows = stockData?.items || [];
    if (!rows.length) {
      toast({ title: 'Nothing to export', description: 'No stock rows in the current view' });
      return;
    }
    const columns = groupBy === 'batch'
      ? [
          { label: 'Product', key: 'product_name' },
          { label: 'SKU', key: 'product_sku' },
          { label: 'Batch', key: 'batch_number' },
          { label: 'Expiry', key: 'expiry_date' },
          { label: 'Qty', key: 'quantity' },
          { label: 'Cost', key: 'cost_price' },
          { label: 'Value', key: 'stock_value' }
        ]
      : [
          { label: 'Product', key: 'product_name' },
          { label: 'SKU', key: 'product_sku' },
          { label: 'Total Qty', key: 'quantity' },
          { label: 'Batches', key: 'batch_count' },
          { label: 'Nearest Expiry', key: 'nearest_expiry' },
          { label: 'Value', key: 'stock_value' }
        ];
    exportRowsToCsv(rows, columns, `stock-${groupBy}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // Download a server-generated PDF honouring the current filters.
  const handleExportPdf = async () => {
    if (!warehouseId) return;
    try {
      const params = {
        warehouse_id: warehouseId,
        view: activeView,
        group_by: groupBy,
        ...(search ? { search } : {}),
        ...(groupBy === 'batch' && batchSearch ? { batch_search: batchSearch } : {}),
        ...(groupBy === 'batch' ? expiryParams(expiryFilter) : {}),
        ...(groupBy === 'batch' ? { include_zero: includeZero } : {}),
      };
      const res = await api.get('/warehouse/export/stock-pdf', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `warehouse-stock-${groupBy}-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'PDF export failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  useEffect(() => {
    fetchStock();
  }, [fetchStock]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (warehouseId) {
        fetchStock();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, warehouseId, fetchStock]);

  const handleViewHistory = async (item) => {
    setSelectedItem(item);
    setMovementsLoading(true);
    setHistoryDialogOpen(true);
    
    try {
      const response = await inventoryAPI.getStockMovements(item.product_id, {
        warehouse_id: warehouseId
      });
      setMovements(response.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch movements', variant: 'destructive' });
    } finally {
      setMovementsLoading(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen" data-testid="warehouse-stock-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Current Stock</h1>
          <p className="text-gray-500">{user?.entity_name} - Inventory Overview</p>
        </div>
        <Button variant="outline" onClick={fetchStock} className="border-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {stockData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Products</p>
                  <p className="text-2xl font-bold text-gray-800">{stockData.summary?.total_items || 0}</p>
                </div>
                <Package className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Quantity</p>
                  <p className="text-2xl font-bold text-gray-800">{stockData.summary?.total_quantity?.toLocaleString() || 0}</p>
                </div>
                <Package className="w-8 h-8 text-teal-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Stock Value</p>
                  <p className="text-2xl font-bold text-teal-600">₹{stockData.summary?.total_value?.toLocaleString() || 0}</p>
                </div>
                <Package className="w-8 h-8 text-teal-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Low Stock</p>
                  <p className="text-2xl font-bold text-amber-600">{stockData.summary?.low_stock_count || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stock Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-4 border-b border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-lg text-gray-800">Stock Items</CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search product..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-gray-50 border-gray-300"
              />
            </div>
          </div>
        </CardHeader>
        
        <Tabs value={activeView} onValueChange={setActiveView}>
          <div className="px-6 pt-4">
            <TabsList className="bg-gray-100">
              <TabsTrigger value="active" className="data-[state=active]:bg-white">
                <Package className="w-4 h-4 mr-2" /> Active
              </TabsTrigger>
              <TabsTrigger value="low" className="data-[state=active]:bg-white">
                <AlertTriangle className="w-4 h-4 mr-2" /> Low Stock
              </TabsTrigger>
              <TabsTrigger value="damaged" className="data-[state=active]:bg-white">
                <Trash2 className="w-4 h-4 mr-2" /> Damaged
              </TabsTrigger>
            </TabsList>
          </div>

          <div className="px-6 pt-4 flex flex-col lg:flex-row lg:items-center gap-3">
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="stock-groupby-switch">
              <button
                type="button"
                onClick={() => setGroupBy('product')}
                className={`px-4 py-2 text-sm transition-colors ${groupBy === 'product' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="stock-groupby-product-btn"
              >
                Product-wise
              </button>
              <button
                type="button"
                onClick={() => setGroupBy('batch')}
                className={`px-4 py-2 text-sm transition-colors ${groupBy === 'batch' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                data-testid="stock-groupby-batch-btn"
              >
                Batch-wise
              </button>
            </div>

            {groupBy === 'batch' && (
              <div className="flex flex-wrap items-center gap-3" data-testid="stock-batch-filters">
                <Input
                  placeholder="Batch / lot number..."
                  value={batchSearch}
                  onChange={(e) => setBatchSearch(e.target.value)}
                  className="w-56 bg-gray-50 border-gray-300"
                  data-testid="stock-batch-search-input"
                />
                <Select value={expiryFilter} onValueChange={setExpiryFilter}>
                  <SelectTrigger className="w-48 bg-gray-50 border-gray-300" data-testid="stock-expiry-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_FILTERS.map(f => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeZero}
                    onChange={(e) => setIncludeZero(e.target.checked)}
                    data-testid="stock-include-zero-toggle"
                  />
                  Show zero-qty batches
                </label>
              </div>
            )}

            <div className="lg:ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExport} data-testid="stock-export-btn">
                <Download className="w-4 h-4 mr-2" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPdf} data-testid="stock-export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" /> PDF
              </Button>
            </div>
          </div>

          <CardContent className="pt-4">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : !stockData?.items?.length ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No {activeView} stock found</p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50 border-gray-200">
                      <TableHead className="text-gray-600">Product</TableHead>
                      <TableHead className="text-gray-600">{groupBy === 'batch' ? 'Batch / Expiry' : 'Batches'}</TableHead>
                      <TableHead className="text-gray-600 text-center">Qty</TableHead>
                      <TableHead className="text-gray-600 text-center">Reorder</TableHead>
                      <TableHead className="text-gray-600 text-right">Cost</TableHead>
                      <TableHead className="text-gray-600 text-right">Value</TableHead>
                      <TableHead className="text-gray-600">Status</TableHead>
                      <TableHead className="text-gray-600 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockData.items.map((item, index) => (
                      <Fragment key={item.stock_id || `${item.product_id}-${index}`}>
                      <TableRow className="border-gray-200">
                        <TableCell>
                          <div>
                            <p className="text-gray-800 font-medium">{item.product_name}</p>
                            <p className="text-teal-600 text-xs">{item.product_sku}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-gray-600 text-sm">
                          {groupBy === 'batch' ? (
                            <>
                              {item.batch_number || 'Default'}
                              {item.expiry_date && (
                                <p className="text-xs text-gray-400">
                                  Exp: {formatDateOnly(item.expiry_date)}
                                </p>
                              )}
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleProductBatches(item)}
                              className="flex items-center gap-1 text-teal-700 hover:underline"
                              data-testid={`stock-expand-batches-${index}`}
                            >
                              {expandedProduct === item.product_id ? (
                                <ChevronDown className="w-3 h-3" />
                              ) : (
                                <ChevronRight className="w-3 h-3" />
                              )}
                              {item.batch_count || 0} batch{(item.batch_count || 0) === 1 ? '' : 'es'}
                              {item.nearest_expiry && (
                                <span className="text-xs text-gray-400 ml-1">
                                  (exp {formatDateOnly(item.nearest_expiry)})
                                </span>
                              )}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className={`text-center font-bold ${
                          item.quantity <= 0 ? 'text-red-600' : 
                          item.is_low_stock ? 'text-amber-600' : 'text-gray-800'
                        }`}>
                          {item.quantity} {item.uom}
                        </TableCell>
                        <TableCell className="text-center text-gray-500">{item.reorder_level}</TableCell>
                        <TableCell className="text-right text-gray-700">₹{item.cost_price?.toFixed(2)}</TableCell>
                        <TableCell className="text-right font-medium text-gray-800">₹{item.stock_value?.toLocaleString()}</TableCell>
                        <TableCell>
                          {item.is_damaged && <Badge className="bg-red-100 text-red-700">Damaged</Badge>}
                          {item.is_low_stock && !item.is_damaged && <Badge className="bg-amber-100 text-amber-700">Low</Badge>}
                          {!item.is_damaged && !item.is_low_stock && item.quantity > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700">OK</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-7 w-7"
                            onClick={() => handleViewHistory(item)} 
                            title="View History"
                          >
                            <History className="w-4 h-4 text-blue-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      {groupBy === 'product' && expandedProduct === item.product_id && (
                        expandedBatches.length === 0 ? (
                          <TableRow className="bg-gray-50" data-testid={`stock-batch-subrow-${index}`}>
                            <TableCell colSpan={8} className="text-sm text-gray-500 pl-8">No batches with stock</TableCell>
                          </TableRow>
                        ) : (
                          expandedBatches.map((b, bi) => (
                            <TableRow key={bi} className="bg-gray-50 border-gray-100" data-testid={bi === 0 ? `stock-batch-subrow-${index}` : undefined}>
                              <TableCell className="pl-8 text-xs text-gray-500">Batch</TableCell>
                              <TableCell className="text-sm font-medium text-gray-800">
                                {b.batch_number}
                                {b.expiry_date && (
                                  <p className="text-xs text-gray-400">Exp: {formatDateOnly(b.expiry_date)}</p>
                                )}
                              </TableCell>
                              <TableCell className="text-center text-sm font-semibold">{b.quantity}</TableCell>
                              <TableCell />
                              <TableCell className="text-right text-sm">₹{(b.cost_price || 0).toFixed(2)}</TableCell>
                              <TableCell className="text-right text-sm">
                                ₹{((b.quantity || 0) * (b.cost_price || 0)).toLocaleString()}
                              </TableCell>
                              <TableCell />
                              <TableCell />
                            </TableRow>
                          ))
                        )
                      )}
                      </Fragment>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Tabs>
      </Card>

      {/* Movement History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">
              Movement History - {selectedItem?.product_name}
            </DialogTitle>
          </DialogHeader>
          
          {movementsLoading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : movements.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No movements found</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-gray-600">Date</TableHead>
                    <TableHead className="text-gray-600">Type</TableHead>
                    <TableHead className="text-gray-600">From</TableHead>
                    <TableHead className="text-gray-600">To</TableHead>
                    <TableHead className="text-gray-600 text-right">Qty</TableHead>
                    <TableHead className="text-gray-600">Reference</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.map((mov, i) => (
                    <TableRow key={i} className="border-gray-200">
                      <TableCell className="text-gray-600">{formatDate(mov.created_at)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{mov.movement_type?.replace('_', ' ')}</Badge>
                      </TableCell>
                      <TableCell className="text-gray-600 capitalize">{mov.from_location_type || '-'}</TableCell>
                      <TableCell className="text-gray-600 capitalize">{mov.to_location_type}</TableCell>
                      <TableCell className="text-right font-medium">{mov.quantity}</TableCell>
                      <TableCell className="text-teal-600">{mov.reference_number || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WarehouseStockPage;
